/* import __COLOCATED_TEMPLATE__ from './company-profile-brands.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

const CLASS_NAME = 'SH-c-company-profile-brands';

class Brands extends Component {
  @service('commands/company-commands') companyCommandService;
  @service('commands.brand-commands') brandCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('commands/image-commands') imageCommandService;

  @service errorTracker;
  @service session;

  @tracked allBrands = [];
  @tracked isAddingBrand = false;

  get className() {
    return CLASS_NAME;
  }

  #mappedCompanyTeamsUsed = createCache(() => {
    return this.allBrands.reduce((map, brand) => {
      brand.companyTeams.forEach((team) => {
        map.set(team.id, team);
      });
      return map;
    }, new Map());
  });

  get mappedCompanyTeamsUsed() {
    return getValue(this.#mappedCompanyTeamsUsed);
  }

  get companyTeams() {
    return this.loadCompany.lastSuccessful?.value.companyTeams ?? [];
  }

  get validUserCompanyTeams() {
    return this.companyTeams.reduce((accum, team) => {
      if (!this.mappedCompanyTeamsUsed.has(team.id)) {
        accum.push(team);
      }

      return accum;
    }, []);
  }

  get hasNoCompanyTeams() {
    return this.companyTeams.length === 0;
  }

  @task
  *initializeData() {
    yield this.loadCompany.perform();
  }

  @task
  *loadCompany() {
    try {
      const response = yield this.companyCommandService.loadCompany(this.args.company.id, ['brands:exclude_default_brands.company-teams', 'company-teams']);
      this.allBrands = response.brands.toArray();
      return response;
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        'There was a problem loading company brands. Please try again or contact support if the problem continues.',
        `loadCompanyBrands -- error loading brands for company id: ${this.args.company.id}`,
        'Error loading the tab'
      );
    }
  }

  @task
  *loadBrand(brandId) {
    try {
      const reloadedBrand =  yield this.brandCommandService.loadBrandById(brandId, ['company-teams']);
      const index = this.allBrands.findIndex((brand) => brand.id === reloadedBrand.id);
      if (index !== -1) {
        this.allBrands[index] = reloadedBrand;
      }
    } catch (error) {
      this.errorTracker.error(`loadBrand failed for brand id ${brandId}`, this, error);
    }
  }

  @task
  *createBrand(brandName, isThirdParty, image) {
    try {
      const newBrand = yield this.brandCommandService.storeBrand(this.args.company.id, brandName, isThirdParty);
      yield this.imageCommandService.associateImage(image, newBrand);
      newBrand.image = image;
      this.allBrands = [newBrand, ...this.allBrands];
      this.allBrands.sort((a, b) => a.name.localeCompare(b.name));

      this.cancelEditBrand();
    } catch (error) {
      this.applicationStateManager.addToast('Failed to create brand');
      this.errorTracker.error(`saveBrandClicked - failed to create brand for company id ${this.args.company.id}`, this, error);
    }
  }

  @action
  updateBrandInArray(updatedBrand) {
    this.allBrands = this.allBrands
      .filter(brand => brand.id !== updatedBrand.id)
      .concat(updatedBrand)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  @action
  removeBrandFromArray(brandId) {
    this.allBrands = this.allBrands.filter(({ id }) => brandId != id);
  }

  @action
  addBrandClicked() {
    this.isAddingBrand = true;
  }

  @action
  cancelEditBrand() {
    this.isAddingBrand = false;
  }
}

export default Brands;
